import { useContext, useEffect, useMemo, useState } from "react";
import {
    Modal, ModalDialog, Stack, Typography, Select, Option, Button,
    Box, Chip, Checkbox, Input, CircularProgress, Alert,
} from "@mui/joy";
import DesignServicesRoundedIcon from '@mui/icons-material/DesignServicesRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';

import {
    APIDraftsmanAssign, APIDraftsmanReassign, APIDraftsmanUnassign,
    APIKonsolEventTasks, APIKonsolEventReleaseTask, APIKonsolEventRevokeTask,
} from "../../api";
import { LayoutContext } from "../../context";
import DrawingRecoveryPanel from "./DrawingRecoveryPanel";
import { draftsmanChoices } from "../../utils/draftsmanChoices";
import { APIDrawingRecovery } from "../../api";
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';

export default function ModalDraftsmanManage({ open, event, employees = [], token, onClose, onDone }) {
    const { employeeAll, getEmployeeAll, notify } = useContext(LayoutContext);
    const current = event?.draftsman || null;                 // текущий чертёжник или null
    const currentSquare = event?.draftsmen_event?.square;     // площадь прошлого (если есть)

    const [recoveryInfo, setRecoveryInfo] = useState(null);
    const [recoveryLoading, setRecoveryLoading] = useState(true);
    const [recoveryError, setRecoveryError] = useState('');
    const [recoveryReload, setRecoveryReload] = useState(0);
    useEffect(() => {
        if (!open || !event?.id) return undefined;
        let cancelled = false;
        setRecoveryLoading(true); setRecoveryInfo(null); setRecoveryError('');
        APIDrawingRecovery(event.id, token).then(data => {
            if (!cancelled) setRecoveryInfo(data);
        }).catch(e => {
            if (!cancelled) setRecoveryError(e.message);
        }).finally(() => { if (!cancelled) setRecoveryLoading(false); });
        return () => { cancelled = true; };
    }, [open, event?.id, token, recoveryReload]);

    const [mode, setMode] = useState('assign');               // assign | reassign | unassign
    const [draftsmanId, setDraftsmanId] = useState(null);
    const [clearPrev, setClearPrev] = useState(false);
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // Отказ по проведённой выплате (409) — это не ошибка ввода, а защита от
    // потери денег. Показываем его отдельной заметной плашкой, иначе менеджер
    // читает его как «я что-то не так заполнил».
    const [blocked, setBlocked] = useState('');
    // Справочник мог вообще не догрузиться — это отдельное состояние,
    // иначе пустой Select выглядит как «чертёжников нет в компании».
    const [listLoading, setListLoading] = useState(false);

    // ── Задание чертёжника в Консоли ────────────────────────────────────────
    // Переназначение само по себе не запрещено, но если у прошлого чертёжника
    // висит ПРИНЯТОЕ задание, Консоль не даст его переписать — и после замены
    // в ней останутся два задания на одно событие. Поэтому показываем задание
    // здесь и предлагаем сначала снять его.
    const [kon, setKon] = useState(null);        // блок draftsman из ручки события
    const [konError, setKonError] = useState('');
    const [konLoading, setKonLoading] = useState(false);
    const [releasing, setReleasing] = useState(false);

    const loadKonsol = async () => {
        if (!event?.id) return;
        setKonLoading(true);
        setKonError('');
        try {
            const d = await APIKonsolEventTasks(event.id, token);
            const role = d?.tasks?.draftsman;
            if (!role) {
                setKon(null);
                setKonError(d?.message || 'Не удалось проверить состояние задания');
            } else {
                setKon(role);
            }
        } catch (e) {
            setKon(null);
            setKonError('Не удалось проверить состояние задания');
        } finally {
            setKonLoading(false);
        }
    };

    useEffect(() => {
        if (!open || !event?.id) { setKon(null); setKonError(''); return; }
        loadKonsol();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, event?.id]);

    const releaseKonsol = async () => {
        const ok = window.confirm(
            `Снять задание #${kon?.task_id} из Консоли?\n\n`
            + `После этого событие можно переназначить, и новому чертёжнику `
            + `создастся своё задание.`
        );
        if (!ok) return;
        setReleasing(true);
        const r = await APIKonsolEventReleaseTask(event.id, 'draftsman', token);
        setReleasing(false);
        if (r?.status !== 'ok') {
            setError(r?.message || 'Не удалось снять задание');
            loadKonsol();
            return;
        }
        notify?.(r.message || 'Задание снято', 'success');
        loadKonsol();
    };

    const revokeKonsol = async () => {
        if (!window.confirm('Отозвать предложение? Назначение чертёжника в событии сохранится.')) return;
        setReleasing(true);
        try {
            const r = await APIKonsolEventRevokeTask(event.id, 'draftsman', token);
            if (r?.status !== 'ok') setError(r?.message || 'Отзыв не подтверждён');
            else notify?.(r.message, 'success');
        } finally { setReleasing(false); loadKonsol(); }
    };

    const draftsmen = useMemo(
        () => draftsmanChoices(employees, employeeAll),
        [employees, employeeAll]
    );

    // Кого реально можно выбрать: текущего исключаем, переназначать на него
    // самого нечего.
    const options = useMemo(
        () => draftsmanChoices(draftsmen, [], current?.id),
        [draftsmen, current]
    );

    useEffect(() => {
        if (!open) return;
        setMode(event?.draftsman ? 'reassign' : 'assign');
        setDraftsmanId(null);
        setClearPrev(false);
        setReason('');
        setError('');
        setBlocked('');
        setLoading(false);
    }, [open, event]);

    // Самолечение: список пуст — просим контекст перезагрузить справочник.
    // Он сам сходит на запасную ручку, если основная закрыта правом.
    useEffect(() => {
        if (!open) return undefined;
        if (draftsmen.length > 0) {
            setListLoading(false);
            return undefined;
        }
        setListLoading(true);
        getEmployeeAll?.();
        // Если и запасная ручка ничего не вернула — через 6 секунд перестаём
        // крутить спиннер и показываем внятную причину вместо пустоты.
        const t = setTimeout(() => setListLoading(false), 6000);
        return () => clearTimeout(t);
    }, [open, draftsmen.length]);

    const fullName = (e) => e
        ? `${e.last_name || ''} ${e.name || ''} ${e.patronymic || ''}`.trim()
        : '—';

    const squareNum = (currentSquare != null && Number(currentSquare) > 0) ? Number(currentSquare) : null;
    const clearLabel = squareNum
        ? `Удалить данные прошлого чертёжника (площадь ${squareNum} м²)`
        : 'Удалить данные прошлого чертёжника (в т.ч. площадь)';

    const handleSubmit = async () => {
        setError('');
        setBlocked('');
        if (mode === 'reassign' && (konLoading || konError || !kon)) {
            setError('Сначала проверьте состояние задания в Консоли');
            return;
        }
        if (mode === 'reassign' && kon?.payment_locked) {
            setBlocked(kon.payment_blocker || 'Отмечена выплата чертёжнику; требуется сверка');
            return;
        }
        if (mode === 'reassign' && kon?.task_id && !kon.can_delete) {
            setError(kon.delete_blocker || 'Снятие старого задания сейчас недоступно'); return;
        }
        let confirmKonsolReplace = false;
        if (mode === 'reassign' && kon?.task_id) {
            confirmKonsolReplace = window.confirm(
                `Снять задание #${kon.task_id} в Консоли и назначить нового чертёжника?\n\n`
                + 'Предложение будет отозвано, черновик удалён. Принятое в работу задание, акт и оплата блокируют замену.'
            );
            if (!confirmKonsolReplace) return;
        }
        setLoading(true);
        let res;
        try {
            if (mode === 'assign') {
                if (!draftsmanId) { setError('Выберите чертёжника'); setLoading(false); return; }
                res = await APIDraftsmanAssign(event.id, draftsmanId, token);
            } else if (mode === 'reassign') {
                if (!draftsmanId) { setError('Выберите нового чертёжника'); setLoading(false); return; }
                res = await APIDraftsmanReassign(
                    event.id, draftsmanId, clearPrev, token, confirmKonsolReplace,
                );
            } else {
                res = await APIDraftsmanUnassign(event.id, reason, token);
            }
        } catch (e) {
            res = { status: 'error', message: 'Сетевая ошибка' };
        }
        setLoading(false);

        if (res?.status === 'ok') {
            if (res?.konsol?.sync_state && res.konsol.sync_state !== 'active') {
                notify?.(res.konsol.message || 'Чертёжник заменён; задание ожидает создания', 'warning');
            }
            onDone?.();
            onClose?.();
            return;
        }

        // Блокировка по проведённой выплате: сервер отвечает 409 и текстом,
        // где сказано, что делать («сначала отмените выплату в разделе
        // “Выплаты”»). Такое сообщение выносим в отдельную плашку.
        const msg = res?.message || 'Не удалось выполнить действие';
        const isPayoutLock = res?.code === 'payout_locked' || /выплат/i.test(msg);
        if (isPayoutLock) setBlocked(msg);
        else setError(msg);
        loadKonsol();
    };

    if (open && (recoveryLoading || recoveryError || recoveryInfo?.historical)) {
        return <Modal open={open} onClose={() => { if (!loading) onClose?.(); }} sx={{ zIndex: 10105, '& .MuiModal-backdrop': { animation: 'recoveryBackdropIn 180ms ease-out', '@keyframes recoveryBackdropIn': { from: { opacity: 0 }, to: { opacity: 1 } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } } }}>
            <ModalDialog sx={{ width: 'min(680px, calc(100vw - 24px))', maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto', p: { xs: 2, sm: 3 }, borderRadius: '20px', boxShadow: 'lg', animation: 'recoveryDialogIn 220ms ease-out', '@keyframes recoveryDialogIn': { from: { opacity: 0, marginTop: '12px' }, to: { opacity: 1, marginTop: 0 } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}>
                {recoveryLoading ? <Typography>Проверяем историю события…</Typography>
                    : recoveryError ? <Stack spacing={1}>
                        <Alert color="danger">{recoveryError}</Alert>
                        <Button onClick={() => setRecoveryReload(v => v + 1)}>Повторить проверку</Button>
                        <Button variant="plain" onClick={onClose}>Закрыть</Button>
                    </Stack>
                    : <DrawingRecoveryPanel key={`${event.id}:${recoveryReload}`} eventId={event.id} token={token}
                        data={recoveryInfo} konsol={kon} onDone={onDone} onClose={onClose} onBusyChange={setLoading}
                        refresh={() => { setRecoveryReload(v => v + 1); loadKonsol(); }} />}
            </ModalDialog>
        </Modal>;
    }

    return (
        <Modal open={open} onClose={() => { if (!loading) onClose?.(); }} sx={{ zIndex: 10105, '& .MuiModal-backdrop': { animation: 'recoveryBackdropIn 180ms ease-out', '@keyframes recoveryBackdropIn': { from: { opacity: 0 }, to: { opacity: 1 } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } } }}>
            <ModalDialog sx={{ maxWidth: 480, width: '100%', borderRadius: '14px' }}>
                <Typography level="title-md" startDecorator={<DesignServicesRoundedIcon />} sx={{ fontWeight: 800, mb: 1 }}>
                    Чертёжник на событии
                </Typography>

                {kon?.can_revoke && <Button size="sm" color="warning" variant="soft" disabled={loading || releasing || konLoading} onClick={revokeKonsol}>Отозвать предложение в Консоли</Button>}
                {kon?.manual_hold && <Typography level="body-xs" color="warning">Автопубликация старого задания остановлена.</Typography>}

                {!!blocked && (
                    <Alert
                        color="warning"
                        variant="soft"
                        startDecorator={<LockRoundedIcon />}
                        sx={{ borderRadius: '10px', mb: 1.5, alignItems: 'flex-start' }}
                    >
                        <Box>
                            <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                                Действие заблокировано
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                {blocked}
                            </Typography>
                        </Box>
                    </Alert>
                )}

                {mode !== 'assign' && konError && (
                    <Alert color="danger" variant="soft" sx={{ mb: 1.5 }}>
                        {konError}. Повторно откройте окно после восстановления связи.
                    </Alert>
                )}
                {mode !== 'assign' && kon?.payment_locked && !blocked && (
                    <Alert color="warning" variant="soft" sx={{ mb: 1.5 }}>
                        {kon.payment_blocker || 'В системе отмечена выплата чертёжнику; нужна сверка'}
                    </Alert>
                )}

                {/* Задание в Консоли у текущего чертёжника.
                    Показываем только в режиме замены/открепления — при первом
                    назначении задания ещё нет и плашка была бы шумом. */}
                {mode !== 'assign' && kon?.task_id && (
                    <Alert
                        color={kon.can_delete ? 'warning' : 'danger'}
                        variant="soft"
                        sx={{ borderRadius: '10px', mb: 1.5, alignItems: 'flex-start' }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                                В Консоли есть задание #{kon.task_id}
                                {kon.konsol_status_label ? ` · ${kon.konsol_status_label}` : ''}
                            </Typography>
                            {kon.can_delete ? (
                                <>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.25 }}>
                                        Сначала снимите его, иначе после замены в Консоли останутся
                                        два задания на одно событие: принятое задание она переписать
                                        не даёт.
                                    </Typography>
                                    <Button
                                        size="sm" variant="soft" color="danger"
                                        loading={releasing}
                                        startDecorator={<LinkOffRoundedIcon fontSize="small" />}
                                        onClick={releaseKonsol}
                                        sx={{ mt: 1 }}
                                    >
                                        Сбросить задачу в Консоли
                                    </Button>
                                </>
                            ) : (
                                <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.25 }}>
                                    Снять его нельзя: {kon.delete_blocker || 'задание закрыто'}.
                                    Замена чертёжника тут уже ничего не исправит — разбирайтесь
                                    с актом и выплатой.
                                </Typography>
                            )}
                        </Box>
                    </Alert>
                )}

                {mode !== 'assign' && !konLoading && kon && !kon.task_id && !kon.payment_locked && (
                    <Alert color="neutral" variant="soft" sx={{ borderRadius: '10px', mb: 1.5 }}>
                        <Typography level="body-xs">
                            Локальная связь с заданием отсутствует. Сервер проверит условия перед заменой.
                        </Typography>
                    </Alert>
                )}

                <Stack spacing={2}>
                    {/* Текущий чертёжник */}
                    {current ? (
                        <Box>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.5 }}>Текущий чертёжник</Typography>
                            <Chip variant="soft" color="warning" startDecorator={<DesignServicesRoundedIcon sx={{ fontSize: 14 }} />}>
                                {fullName(current)}{squareNum ? ` · ${squareNum} м²` : ''}
                            </Chip>
                        </Box>
                    ) : (
                        <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'background.level1' }}>
                            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Чертёжник не назначен</Typography>
                        </Box>
                    )}

                    {/* Выбор действия (только если есть текущий) */}
                    {current && (
                        <Stack direction="row" spacing={1}>
                            <Button size="sm" variant={mode === 'reassign' ? 'solid' : 'soft'} color="primary"
                                    onClick={() => setMode('reassign')} sx={{ borderRadius: '10px', flex: 1 }}>
                                Переназначить
                            </Button>
                            <Button size="sm" variant={mode === 'unassign' ? 'solid' : 'soft'} color="danger"
                                    onClick={() => setMode('unassign')} sx={{ borderRadius: '10px', flex: 1 }}>
                                Открепить
                            </Button>
                        </Stack>
                    )}

                    {/* Выбор чертёжника */}
                    {(mode === 'assign' || mode === 'reassign') && (
                        <Box>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>
                                {mode === 'assign' ? 'Чертёжник' : 'Новый чертёжник'}
                            </Typography>

                            {listLoading ? (
                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', py: 0.75 }}>
                                    <CircularProgress size="sm" />
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                        Загружаем список чертёжников…
                                    </Typography>
                                </Stack>
                            ) : options.length === 0 ? (
                                <Alert color="neutral" variant="soft" sx={{ borderRadius: '10px', alignItems: 'flex-start' }}>
                                    <Box>
                                        <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                                            Некого выбрать
                                        </Typography>
                                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                            {draftsmen.length === 0
                                                ? 'Справочник исполнителей не загрузился. Обновите страницу — если не поможет, напишите администратору.'
                                                : 'Кроме текущего чертёжника, свободных нет.'}
                                        </Typography>
                                    </Box>
                                </Alert>
                            ) : (
                                <Select size="sm" value={draftsmanId} placeholder="Выберите чертёжника"
                                        onChange={(_, v) => setDraftsmanId(v)} sx={{ borderRadius: '10px' }}
                                        slotProps={{ listbox: { sx: { zIndex: 10106 } } }}>
                                    {options.map(e => (
                                        <Option key={e.id} value={e.id}>
                                            {e.last_name || ''} {e.name}
                                            {e.city_bitrix_name ? ` · ${e.city_bitrix_name}` : ''}
                                        </Option>
                                    ))}
                                </Select>
                            )}
                        </Box>
                    )}

                    {/* Чекбокс удаления данных прошлого — только при переназначении */}
                    {mode === 'reassign' && (
                        <Box>
                            <Checkbox size="sm" label={clearLabel} checked={clearPrev}
                                      onChange={(e) => setClearPrev(e.target.checked)} />
                            {!clearPrev && (
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                                    Площадь прошлого чертёжника будет перенесена новому.
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Причина открепления — опционально */}
                    {mode === 'unassign' && (
                        <Box>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>Причина (необязательно)</Typography>
                            <Input size="sm" value={reason} onChange={(e) => setReason(e.target.value)}
                                   placeholder="Например: перегрузка чертёжника" sx={{ borderRadius: '10px' }} />
                        </Box>
                    )}

                    {error && <Typography level="body-sm" color="danger">{error}</Typography>}

                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button variant="soft" color="neutral" onClick={onClose} disabled={loading}
                                sx={{ borderRadius: '10px', flex: 1 }}>
                            Отмена
                        </Button>
                        <Button variant="solid" color={mode === 'unassign' ? 'danger' : 'primary'}
                                onClick={handleSubmit} loading={loading}
                                disabled={loading || (mode !== 'unassign' && options.length === 0)
                                    || (mode === 'reassign' && (konLoading || !!konError || !kon
                                        || !!kon.payment_locked || (!!kon.task_id && !kon.can_delete)))}
                                
                                sx={{ borderRadius: '10px', flex: 2, fontWeight: 700 }}>
                            {mode === 'assign' ? 'Назначить' : mode === 'reassign' ? 'Переназначить' : 'Открепить'}
                        </Button>
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}